import React, { useState } from 'react';
import { questionPaperToText } from '../utils/questionPaper';
import { copyToClipboard } from '../utils/clipboard';
import { useAuth } from '../contexts/AuthContext';
import QuestionPaperPrintPreview from './QuestionPaperPrintPreview';
import FractionText from './FractionText';
import PassageContent from './PassageContent';
import './QuestionPaperPreview.css';

export default function QuestionPaperPreview({ paper, onClose }) {
  const { currentUser } = useAuth();
  const canPrint = currentUser?.role !== 'teacher';
  const [copied, setCopied] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  if (!paper) return null;

  const rtl = paper.dir === 'rtl';
  const ready = paper.questionCount > 0;

  const handleCopy = () => {
    const text = questionPaperToText(paper);
    if (!text) return;
    copyToClipboard(text).then((ok) => {
      if (!ok) return;
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="qp-overlay" onClick={onClose}>
      <div
        className="qp-dialog"
        role="dialog"
        aria-label={rtl ? 'سوالنامہ' : 'Question paper'}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="qp-toolbar">
          <div className="qp-toolbar-title">
            <i className="fas fa-print"></i>
            Question paper
            {ready && (
              <span className="qp-count">
                {paper.questionCount} question{paper.questionCount === 1 ? '' : 's'}
              </span>
            )}
          </div>
          <div className="qp-toolbar-actions">
            <button type="button" className="qp-btn" onClick={handleCopy} disabled={!ready}>
              <i className={copied ? 'fas fa-check' : 'fas fa-copy'}></i>
              {copied ? 'Copied' : 'Copy'}
            </button>
            {canPrint && (
              <button type="button" className="qp-btn qp-btn--print" onClick={() => setPrintOpen(true)} disabled={!ready}>
                <i className="fas fa-print"></i>
                Print
              </button>
            )}
            <button type="button" className="qp-btn qp-btn--close" onClick={onClose}>
              Close
            </button>
          </div>
        </div>

        {ready ? (
          <div className={`qp-sheet${rtl ? ' qp-sheet--rtl' : ''}`} dir={rtl ? 'rtl' : 'ltr'}>
            {(paper.className || paper.subject || paper.dateLabel || paper.totalMarks) ? (
              <div className="qp-meta">
                <div className="qp-meta-side">
                  {paper.className ? <span>{rtl ? 'جماعت' : 'Class'}: {paper.className}</span> : null}
                  {paper.subject ? <span>{rtl ? 'مضمون' : 'Subject'}: {paper.subject}</span> : null}
                </div>
                <div className="qp-total">
                  {paper.totalMarks ? `${rtl ? 'کل نمبر' : 'Total Marks'}: ${paper.totalMarks}` : ''}
                </div>
                <div className="qp-meta-side qp-meta-side--end">
                  {paper.dateLabel ? <span>{rtl ? 'تاریخ' : 'Date'}: {paper.dateLabel}</span> : null}
                </div>
              </div>
            ) : null}
            <div className="qp-fields">
              <span>{rtl ? 'رول نمبر' : 'Roll Number'}:</span>
              <span>{rtl ? 'نام' : 'Name'}:</span>
            </div>
            {paper.blocks.map((block, i) => (
              block.type === 'section' ? (
                <h2 key={i} className="qp-section">{block.text}</h2>
              ) : block.type === 'passage' ? (
                <div key={i} className="qp-passage">
                  <PassageContent
                    lines={String(block.text || '').split('\n')}
                    parts={block.passageParts}
                    underClass="qp-ul"
                    tableClass="qp-data"
                  />
                </div>
              ) : (
                <div key={i}>
                  <p className="qp-question">
                    {block.label ? <strong>{block.label}</strong> : null}
                    {block.label ? ` ${block.sep} ` : ''}
                    {(block.restParts || [{ text: block.rest, under: false }]).map((bit, j) => (
                      bit.under
                        ? <span key={j} className="qp-ul"><FractionText text={bit.text} /></span>
                        : <span key={j}><FractionText text={bit.text} /></span>
                    ))}
                  </p>
                  {block.passage && block.passage.length ? (
                    <div className="qp-passage">
                      <PassageContent
                        lines={block.passage}
                        parts={block.passageParts}
                        underClass="qp-ul"
                        tableClass="qp-data"
                      />
                    </div>
                  ) : null}
                </div>
              )
            ))}
          </div>
        ) : (
          <div className="qp-empty">
            No questions were found in this rubric. The paper needs lines like
            {' '}<code>Q.1 - … (2 marks)</code>.
          </div>
        )}
      </div>
      {printOpen && (
        <QuestionPaperPrintPreview paper={paper} onClose={() => setPrintOpen(false)} />
      )}
    </div>
  );
}
